import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LifeBuoy, Plus, Loader2, Send, MessageSquare } from 'lucide-react';

const CATEGORIES = [
  { v: 'power', l: 'No power / outage' },
  { v: 'billing', l: 'Billing / top-up' },
  { v: 'meter', l: 'Meter problem' },
  { v: 'app', l: 'App / login' },
  { v: 'other', l: 'Other' },
];
const STATUS_STYLE: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700', closed: 'bg-gray-200 text-gray-600',
};
const fmt = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

interface Ticket { id: string; subject: string; category: string; description: string; priority: string; status: string; created_at: string; }
interface Reply { id: string; ticket_id: string; is_staff: boolean; message: string; created_at: string; }

const SupportTicketWidget: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'power', priority: 'normal', description: '' });
  const [view, setView] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('support_tickets').select('*').order('created_at', { ascending: false });
    setTickets((data || []) as Ticket[]);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) { toast.error('Add a subject and a description'); return; }
    if (!user?.id) { toast.error('Please sign in again'); return; }
    setSaving(true);
    // Best-effort context: org + apartment from the user's profile.
    const { data: prof } = await supabase.from('users').select('organization_id, apartment_id').eq('id', user.id).maybeSingle();
    const { error } = await supabase.from('support_tickets').insert({
      user_id: user.id, organization_id: prof?.organization_id ?? null, apartment_id: prof?.apartment_id ?? null,
      subject: form.subject.trim(), category: form.category, priority: form.priority, description: form.description.trim(),
    });
    setSaving(false);
    if (error) { toast.error('Could not submit: ' + error.message); return; }
    toast.success('Ticket submitted — our team will get back to you.');
    setOpen(false); setForm({ subject: '', category: 'power', priority: 'normal', description: '' });
    load();
  };

  const openTicket = async (t: Ticket) => {
    setView(t); setReplies([]);
    const { data } = await supabase.from('ticket_replies').select('*').eq('ticket_id', t.id).order('created_at');
    setReplies((data || []) as Reply[]);
  };

  const sendReply = async () => {
    if (!view || !replyText.trim()) return;
    setReplying(true);
    const { error } = await supabase.from('ticket_replies').insert({ ticket_id: view.id, author_id: user?.id ?? null, is_staff: false, message: replyText.trim() });
    setReplying(false);
    if (error) { toast.error('Could not send: ' + error.message); return; }
    setReplyText('');
    openTicket(view);
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-blue-600" /> Raise an issue</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> New ticket</Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">Open a support ticket and track its progress here.</p>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No tickets yet. Tap “New ticket” to raise one.</p>
        ) : (
          <div className="divide-y">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t)} className="w-full text-left py-2.5 flex items-center justify-between gap-2 hover:bg-muted/50 rounded px-1">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.subject}</p>
                  <p className="text-xs text-muted-foreground">{CATEGORIES.find((c) => c.v === t.category)?.l || t.category} · {fmt(t.created_at)}</p>
                </div>
                <Badge className={`${STATUS_STYLE[t.status]} shrink-0 text-[10px]`}>{t.status.replace('_', ' ')}</Badge>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {/* New ticket */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New support ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Short summary of the issue" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Describe the issue</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell us what's happening…" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit ticket'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View ticket + thread */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2 pr-6">{view?.subject} {view && <Badge className={`${STATUS_STYLE[view.status]} text-[10px]`}>{view.status.replace('_', ' ')}</Badge>}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3">
              <div className="text-sm bg-muted/50 rounded p-2">{view.description}</div>
              <div className="space-y-2 max-h-64 overflow-auto">
                {replies.length === 0 ? <p className="text-xs text-muted-foreground text-center py-2">No replies yet.</p> :
                  replies.map((r) => (
                    <div key={r.id} className={`text-sm rounded p-2 ${r.is_staff ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-muted/50'}`}>
                      <p className="text-[11px] text-muted-foreground mb-0.5 flex items-center gap-1">
                        {r.is_staff && <MessageSquare className="w-3 h-3" />}{r.is_staff ? 'Support' : 'You'} · {fmt(r.created_at)}
                      </p>
                      {r.message}
                    </div>
                  ))}
              </div>
              {view.status !== 'closed' && (
                <div className="flex gap-2">
                  <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Add a reply…" onKeyDown={(e) => e.key === 'Enter' && sendReply()} />
                  <Button size="icon" onClick={sendReply} disabled={replying || !replyText.trim()}>{replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SupportTicketWidget;
